<template>
  <BaseDialog
    :title="title"
    :close-on-click-modal="false"
    width="700px"
    @close="$emit('close')"
    @confirm="onConfirm"
    @keydown.enter.prevent
    @keydown.enter.native.prevent
  >
    <div v-loading="loading">
      <StructureFilters
        v-if="config.filters.length"
        :filters="config.filters"
        :models="filterModels"
      />
      <div class="data-table">
        <div
          class="grid-container text-primary-100"
          style="padding-top: 21px; padding-bottom: 16px"
          :style="`grid-template-columns: 50px repeat(${displayColumns.length}, 1fr)`"
        >
          <BaseElCheckbox
            v-model="selectAll"
            class="px-[20px]"
            :disabled="hasSelectionLimit"
            @change="onSelectAllChange"
          />
          <p
            v-for="(column, index) of displayColumns"
            :key="column.label"
            class="font-bold"
            :class="index === 0 ? 'text-left' : 'text-center'"
          >
            {{ column.label }}
          </p>
        </div>
        <BaseElCheckboxGroup
          v-model="multipleSelection"
          class="bg-white"
          @change="onSelectionChange"
        >
          <EmptyBlock v-if="!displayTableData.length" />
          <div v-for="item in displayTableData" :key="item.id">
            <hr>
            <div
              class="grid-container row"
              :class="isSelectionDisabled(item) ? 'row-disabled' : 'row-clickable'"
              :style="`grid-template-columns: 50px repeat(${displayColumns.length}, 1fr)`"
              @click="onRowClick(item, $event)"
            >
              <BaseElCheckbox
                class="px-[20px]"
                :label="item.id"
                :disabled="isSelectionDisabled(item)"
              >
                <p />
              </BaseElCheckbox>
              <component
                :is="column.component ?? 'div'"
                v-for="(column, index) of displayColumns"
                :key="column.prop"
                :class="index === 0 ? 'text-left' : 'text-center'"
                v-bind="isFunction(column.props) ? column.props(item) : column.props"
              >
                <div v-if="column.render">{{ column.render(item) }}</div>
              </component>
            </div>
          </div>
        </BaseElCheckboxGroup>
      </div>
      <Pagination
        :curPage.sync="tableOptions.page"
        :pageLimit="tableOptions.pageLimit"
        :total="tableDataCount"
      />
    </div>
  </BaseDialog>
</template>

<script>
import { computed, defineComponent, onBeforeMount, reactive, ref, set, watch } from 'vue'
import BaseDialog from '../Dialog/BaseDialog.vue'
import { map, filter, every, includes, chunk, get, find, isFunction, isNumber } from 'lodash'
import StructureFilters from '@/components/StructureFilters.vue'
import EmptyBlock from '@/components/EmptyBlock.vue'

export default defineComponent({
  name: 'AdvanceTableSelector',
  components: {
    BaseDialog,
    StructureFilters,
    EmptyBlock,
  },
  props: {
    title: { type: String },
    data: { type: Array, default: () => [] },
    config: {
      type: Object,
      default: () => ({
        selected: [],
        columns: { type: Array, default: () => [] },
        filters: { type: Array, default: () => [] },
        disableRow: null,
        pageLimit: 8,
      }),
    },
    loading: { type: Boolean, default: false },
  },
  setup (props, { emit }) {
    const selectAll = ref(false)
    const multipleTable = ref(null)
    const multipleSelection = ref([])
    const filterModels = reactive({})
    const tableOptions = reactive({
      page: 1,
      pageLimit: 8,
    })

    const maxSelection = computed(() => {
      return isNumber(props.config.maxSelection) && props.config.maxSelection > 0
        ? props.config.maxSelection
        : null
    })

    const hasSelectionLimit = computed(() => maxSelection.value !== null)

    const hasDisableRow = computed(() => isFunction(props.config.disableRow))

    const displayColumns = computed(() => {
      return map(props.config.columns, (column) => {
        return {
          prop: column.key,
          label: column.label,
          align: 'left',
          component: column.component,
          props: column.props,
          render: (row) => typeof column.renderer === 'function' ? column.renderer(row) : null,
        }
      })
    })

    const tableData = computed(() => {
      // 根據 filters 過濾 data
      // 根據每個 filter.mode 來決定過濾方式: includes, equals, range
      const filterData = filter(props.data, (item) => {
        return every(props.config.filters, (filter) => {
          const value = (filterModels[filter.key]) ?? ''
          if (value === '') return true // 如果沒有值則不過濾
          switch (filter.mode) {
          case 'includes':
            return includes(item[filter.key], value)
          case 'equals':
            return item[filter.key] === value
          case 'custom':
            return filter.filter(item, filterModels)
          default:
            return true
          }
        })
      })
      return map(filterData, (item) => {
        return {
          ...item,
          selected: includes(multipleSelection.value, item.id),
        }
      })
    })

    const tableDataCount = computed(() => {
      return tableData.value.length
    })

    const displayTableData = computed(() => {
      // 根據 tableOptions.page 和 tableOptions.pageLimit 分頁
      // return filterData
      const tablePageData = chunk(tableData.value, tableOptions.pageLimit)
      return get(tablePageData, tableOptions.page - 1, [])
    })

    const isRowDisabledByConfig = (item) => {
      if (!hasDisableRow.value) return false
      return props.config.disableRow(item) === true
    }

    const selectableIds = computed(() => {
      return map(filter(tableData.value, (item) => !isRowDisabledByConfig(item)), 'id')
    })

    const isAllSelected = computed(() => {
      if (!selectableIds.value.length || hasSelectionLimit.value) return false
      return every(selectableIds.value, (id) => includes(multipleSelection.value, id))
    })

    const initFilterModels = () => {
      for (const filter of props.config.filters) {
        if (filter.key) {
          set(filterModels, filter.key, filter.defaultValue || '')
        }
      }
    }

    const onSelectAllChange = (toggle) => {
      if (hasSelectionLimit.value) {
        selectAll.value = false
        return
      }

      if (toggle) {
        // 如果全選，則將所有可見數據的 id 添加到 multipleSelection 中
        const allData = map(filter(tableData.value, (item) => !isSelectionDisabled(item)), 'id')
        multipleSelection.value = [...new Set(allData)]
      } else {
        // 如果取消全選，則清空 multipleSelection
        multipleSelection.value = []
      }
    }

    const onSelectionChange = (selection) => {
      if (hasSelectionLimit.value && selection.length > maxSelection.value) {
        multipleSelection.value = selection.slice(0, maxSelection.value)
      } else {
        multipleSelection.value = selection
      }
      emit('select-change', multipleSelection.value)
    }

    const isSelectionDisabled = (item) => {
      if (includes(multipleSelection.value, item.id)) return false
      if (isRowDisabledByConfig(item)) return true
      if (!hasSelectionLimit.value) return false
      return multipleSelection.value.length >= maxSelection.value
    }

    const onRowClick = (item, event) => {
      if (event?.target?.closest?.('input, button, a, textarea, select, label, .el-checkbox')) return
      if (isSelectionDisabled(item)) return

      const isSelected = includes(multipleSelection.value, item.id)
      const nextSelection = isSelected
        ? multipleSelection.value.filter((id) => id !== item.id)
        : [...multipleSelection.value, item.id]

      onSelectionChange(nextSelection)
    }

    const onConfirm = () => {
      emit('confirm', map(multipleSelection.value, (id) => {
        return find(props.data, (item) => item.id === id)
      }))
      emit('close')
    }

    onBeforeMount(() => {
      initFilterModels()
      tableOptions.pageLimit = props.config.pageLimit || 8
      multipleSelection.value = props.config.selected || []
    })

    watch(isAllSelected, (value) => {
      selectAll.value = value
    }, { immediate: true })

    return {
      hasSelectionLimit,
      selectAll,
      onConfirm,
      tableDataCount,
      tableOptions,
      multipleTable,
      displayColumns,
      filterModels,
      displayTableData,
      multipleSelection,
      includes,
      selectableIds,
      isSelectionDisabled,
      onRowClick,
      onSelectAllChange,
      onSelectionChange,
      isFunction,
    }
  },
})
</script>

<style lang="postcss" scoped>
.grid-container {
  @apply grid gap-[20px];
  @apply text-normal;
}
.data-table {
  @apply max-h-[550px] overflow-y-auto;
}
.row {
  @apply py-[16px] items-center
}
.row-clickable {
  @apply cursor-pointer;
}
.row-disabled {
  @apply cursor-not-allowed opacity-50;
}
.row:hover {
  @apply bg-[#F5F7FA];
}
.row-disabled:hover {
  @apply bg-transparent;
}
</style>
